import { model, type SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { ChangeDetectorRef, Component, Input, input } from '@angular/core';

import { trackByValue, type WrapModels } from '@core/common-legacy';
import { objectKeys } from '@core/utils-legacy';

import type { RtButtonVariant } from '../../../common/button/button.types';
import { ButtonComponent } from '../../../common/button/components/button/button.component';
import { DividerComponent } from '../../../common/divider/divider.component';
import { GroupComponent } from '../../../common/group/group.component';
import { IconComponent } from '../../../common/icon/icon.component';
import { TagComponent } from '../../../common/tag/tag.component';
import { CheckboxComponent } from '../../../forms/checkbox/checkbox.component';
import { ConfirmDialogVariant } from '../../dialogs.types';
import { DialogHeaderComponent } from '../dialog/dialog-header/dialog-header.component';
import { DialogRef } from '../dialog/dialog-ref';
import type { DialogComponentI } from '../dialog/dialog.component';

export type ConfirmDialogData = {
  title: string;
  message: string;
  footerMessage: string;
  isCancelable: boolean;
  variant: string;
  safeFooter: boolean;
  checkboxes: Array<{ title: string; value?: boolean }>;
  emphasizeLastCheckbox: boolean;
  agreeLabel: string;
  cancelLabel: string;
  showMoreLabel: string;
  showLessLabel: string;
  tags: string[];
  list: string[];
  confirmActionsLayout: 'end' | 'space-between';
};

type ConfirmResult = ConfirmPayload | boolean;

interface ConfirmPayload {
  confirmed: boolean;
}

const SignalInputsKeys = <const>[
  'title',
  'isCancelable',
  'variant',
  'safeFooter',
  'checkboxes',
  'emphasizeLastCheckbox',
  'cancelLabel',
  'showMoreLabel',
  'showLessLabel',
  'tags',
  'confirmActionsLayout',
];

type SignalInputs = (typeof SignalInputsKeys)[number];
const isSignalInput = (key: string): key is SignalInputs =>
  SignalInputsKeys.includes(<SignalInputs>key);

@Component({
  selector: 'rt-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.less'],
  host: {
    '[class]': 'variant()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    CheckboxComponent,
    DialogHeaderComponent,
    DividerComponent,
    GroupComponent,
    IconComponent,
    TagComponent,
  ],
})
export class ConfirmDialogComponent
  implements
    DialogComponentI<ConfirmDialogData, ConfirmResult>,
    WrapModels<ConfirmDialogData, SignalInputs>
{
  private readonly TAGS_LIMIT: number = 9;

  public readonly buttonVariantMap: Record<ConfirmDialogVariant, RtButtonVariant> = {
    error: 'error',
    information: 'informative',
    success: 'success',
    warning: 'warning',
  };

  public readonly title = model<string>(undefined!);
  public readonly isCancelable = model<boolean>(true);
  public readonly variant = model<ConfirmDialogVariant>(undefined!);
  public readonly safeFooter = model<boolean>(false);
  public readonly checkboxes = model<
    Array<{
      title: string;
      value?: boolean;
    }>
  >([]);
  public readonly emphasizeLastCheckbox = model<boolean>(true);
  public readonly cancelLabel = model<string>(undefined!);
  public readonly showMoreLabel = model<string>(undefined!);
  public readonly showLessLabel = model<string>(undefined!);
  public readonly tags = model<string[]>([]);
  public readonly confirmActionsLayout = model<'end' | 'space-between'>('end');

  public readonly triggerUpdateOnInit = input<boolean>(false);
  public readonly closePayload = input<{
    payload: any;
  }>();

  @Input() public agreeLabel: string;
  @Input() public list: string[] = [];
  @Input() public message: string;
  @Input() public footerMessage: string;

  public ConfirmDialogVariant: typeof ConfirmDialogVariant = ConfirmDialogVariant;
  public visibleTags: string[] = [];
  public tagsCollapseActive: boolean = false;
  public tagsCollapsed: boolean = false;
  public trackByValue = trackByValue;
  public tmpCheckboxes: Array<{ label: string; value: boolean }> = [];

  constructor(
    private dialogRef: DialogRef<ConfirmDialogComponent, ConfirmResult>,
    protected cdRef: ChangeDetectorRef,
  ) {
    this.cdRef.detach();
  }

  public setDialogData(data: ConfirmDialogData, triggerUpdate: boolean): void {
    objectKeys(data).forEach(key => {
      if (isSignalInput(key)) {
        // @ts-ignore
        this[key].set(data[key]);
      } else {
        // @ts-ignore
        this[key] = data[key];
      }
    });

    const tags = this.tags();
    if (tags) {
      this.prepareTagsToDisplay(tags);
    }

    if (triggerUpdate) {
      this.cdRef.detectChanges();
    }
  }

  public ngOnInit(): void {
    if ((this.checkboxes()?.length ?? 0) > 0) {
      this.tmpCheckboxes = this.checkboxes().map(option => ({
        label: option.title,
        value: !!option.value,
      }));
      this.cdRef.detectChanges();
    }
    if (this.triggerUpdateOnInit()) {
      this.cdRef.detectChanges();
    }
  }

  public get isFormValid(): boolean {
    return !this.tmpCheckboxes.some(option => !option.value);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.tags?.currentValue) {
      this.prepareTagsToDisplay(this.tags());
    }
  }

  public handleCheckboxValueChange(index: number, value: boolean): void {
    this.tmpCheckboxes = this.tmpCheckboxes.map((option, i) => {
      if (i === index) {
        return { ...option, value };
      } else {
        return option;
      }
    });
    this.cdRef.detectChanges();
  }

  public confirm(): void {
    const closePayload = this.closePayload();
    const result = closePayload
      ? {
          confirmed: true,
          ...closePayload,
        }
      : true;
    this.dialogRef.close(result);
  }

  public cancel(): void {
    const closePayload = this.closePayload();
    const result = closePayload
      ? {
          confirmed: false,
          ...closePayload,
        }
      : false;
    this.dialogRef.close(result);
  }

  public trackByCheckbox(index: number): number {
    return index;
  }

  public toggleTagsCollapse(): void {
    this.tagsCollapsed = !this.tagsCollapsed;
    if (this.tagsCollapsed) {
      this.visibleTags = this.tags().slice(0, this.TAGS_LIMIT);
    } else {
      this.visibleTags = [...this.tags()];
    }
    this.cdRef.detectChanges();
  }

  private prepareTagsToDisplay(tags: string[] = this.tags()): void {
    this.visibleTags = tags.slice(0, this.TAGS_LIMIT);
    if (this.visibleTags.length < this.tags().length) {
      this.tagsCollapsed = true;
      this.tagsCollapseActive = true;
    }
  }
}
